/* eslint formatjs/no-literal-string-in-jsx: "error" */
import { defineMessage, MessageDescriptor } from "react-intl"
import { useMemo } from "react"
import { reportErrorMessage } from "../../../utils/ErrorReporting"
import { Benefit } from "../Benefit"

const OPTIONS = [
  {
    label: defineMessage({ defaultMessage: "Adoption" }),
    labelLowercase: defineMessage({ defaultMessage: "adoption" }),
    value: "ADOPTION"
  },
  {
    label: defineMessage({ defaultMessage: "Working with a gestational carrier" }),
    labelLowercase: defineMessage({ defaultMessage: "working with a gestational carrier" }),
    value: "GESTATIONAL"
  },
  {
    label: defineMessage({ defaultMessage: "Fertility preservation (e.g., freezing eggs or sperm)" }),
    labelLowercase: defineMessage({ defaultMessage: "fertility preservation (e.g., freezing eggs or sperm)" }),
    value: "PRESERVATION"
  },
  {
    label: defineMessage({ defaultMessage: "Assisted reproduction (e.g., IUI or IVF)" }),
    labelLowercase: defineMessage({ defaultMessage: "assisted reproduction (e.g., IUI or IVF)" }),
    value: "ASSISTED_REPRODUCTION"
  },
  {
    label: defineMessage({ defaultMessage: "Trying to get pregnant (e.g., timed intercourse)" }),
    labelLowercase: defineMessage({ defaultMessage: "trying to get pregnant (e.g., timed intercourse)" }),
    value: "TRY_PREGNANT"
  },
  {
    label: defineMessage({ defaultMessage: "Exploring family-forming options" }),
    labelLowercase: defineMessage({ defaultMessage: "exploring family-forming options" }),
    value: "EXPLORING"
  },
  {
    label: defineMessage({ defaultMessage: "Currently pregnant" }),
    labelLowercase: defineMessage({ defaultMessage: "currently pregnant" }),
    value: "PREGNANT"
  },
  {
    label: defineMessage({ defaultMessage: "Menopause" }),
    labelLowercase: defineMessage({ defaultMessage: "menopause" }),
    value: "MENOPAUSE"
  },
  {
    label: defineMessage({ defaultMessage: "Low testosterone" }),
    labelLowercase: defineMessage({ defaultMessage: "low testosterone" }),
    value: "LOW_TESTOSTERONE"
  },
  {
    label: defineMessage({ defaultMessage: "Gender-affirming care" }),
    labelLowercase: defineMessage({ defaultMessage: "gender-affirming care" }),
    value: "GENDER_AFFIRMING_CARE"
  },
  {
    label: defineMessage({ defaultMessage: "Something else" }),
    labelLowercase: defineMessage({ defaultMessage: "something else" }),
    value: "SOMETHING_ELSE"
  }
]

export function useJourneyOptions(
  memberBenefit: Benefit,
  enableMemberJourneyModularity = false,
  enablePostpartumRoutingSupport = false
): { options: { label: MessageDescriptor; labelLowercase: MessageDescriptor; value: string }[] } {
  const options = useMemo(
    () =>
      OPTIONS.filter(({ value }, index) => {
        if (memberBenefit) {
          // WQ-1470, when removing the feature flag delete this if check and update the original PREGNANT item in the OPTIONS array.
          if (value === "PREGNANT") {
            enablePostpartumRoutingSupport
              ? (OPTIONS[index].label = defineMessage({ defaultMessage: "Pregnant or parenting an infant" })) &&
                (OPTIONS[index].labelLowercase = defineMessage({ defaultMessage: "pregnant or parenting an infant" }))
              : (OPTIONS[index].label = defineMessage({ defaultMessage: "Currently pregnant" })) &&
                (OPTIONS[index].labelLowercase = defineMessage({ defaultMessage: "currently pregnant" }))
          }

          const journeys = memberBenefit.journeys
          switch (value) {
            case "ADOPTION": {
              return journeys.adoption.isAppSupportEnabled
            }
            case "GESTATIONAL": {
              return journeys.gestationalCarrier.isAppSupportEnabled
            }
            case "PRESERVATION": {
              // until journey modularity is live, the enableMemberJourneyModularity flag keeps the existing behavior
              // where certain journeys are always shown to members
              return enableMemberJourneyModularity ? journeys.fertilityPreservation.isAppSupportEnabled : true
            }
            case "ASSISTED_REPRODUCTION": {
              return enableMemberJourneyModularity ? journeys.fertilityCare.isAppSupportEnabled : true
            }
            case "TRY_PREGNANT": {
              return enableMemberJourneyModularity
                ? journeys.fertilityCare.isAppSupportEnabled || journeys.fertilityPreservation.isAppSupportEnabled
                : true
            }
            case "EXPLORING": {
              return enableMemberJourneyModularity
                ? journeys.fertilityCare.isAppSupportEnabled || journeys.fertilityPreservation.isAppSupportEnabled
                : true
            }
            case "PREGNANT": {
              return journeys.pregnancy.isAppSupportEnabled
            }
            case "MENOPAUSE": {
              return journeys.menopause.isAppSupportEnabled
            }
            case "LOW_TESTOSTERONE": {
              return journeys.lowTestosterone.isAppSupportEnabled
            }
            case "GENDER_AFFIRMING_CARE": {
              return journeys.genderAffirmingCare.isAppSupportEnabled
            }
            case "SOMETHING_ELSE": {
              return true
            }
            default: {
              // Journey is in options but not set up here with the member benefit
              reportErrorMessage(`Journey ${value} is not yet set up.`)
              return false
            }
          }
        }
        return false
      }),
    [memberBenefit, enableMemberJourneyModularity, enablePostpartumRoutingSupport]
  )

  return {
    options
  }
}
